import { usePostHog } from "@posthog/react";
import type { CaptureResult, PostHogConfig } from "posthog-js";
import { useCallback } from "react";

import { scrubShareCodes } from "./sharePaths";

/**
 * Product analytics for fazly.app.
 *
 * The site exists to hand people to the app: a shared `/r/…` or `/scan/…` link
 * opens the app directly when it is installed, so ANYONE who reaches this
 * website through one is by definition someone who does not have it. That makes
 * these pages the top of the install funnel, and the web step is the one part
 * of it nothing else can see — whatever eventually records the share going out
 * does so inside the app, and the arrival lands here.
 *
 * The same three restraints the mobile client works under (see
 * apps/mobile/src/lib/analytics.ts), for the same reasons:
 *
 *  1. NO USER CONTENT, and here that is stricter than a preference. The share
 *     routes carry a referral code or a scan id in the path, and
 *     `ShareLanding` deliberately renders nothing about either. Sending that
 *     value to PostHog would put a per-person identifier in an analytics store
 *     that the page itself refuses to resolve — so events carry the KIND of
 *     thing shared, never which one.
 *  2. NO PERSONAL DATA. Nobody is signed in here; there is no device id to
 *     identify against and none is invented.
 *  3. FEW, DELIBERATE EVENTS. PostHog bills per event and is already this
 *     product's largest infrastructure line at scale.
 */

/**
 * The PostHog project token — public by design (it is `phc_`-prefixed, ships in
 * every website's JavaScript, and can only write events).
 *
 * ── Why there IS a committed default now ─────────────────────────────────────
 *
 * There deliberately was not one, and the reason has been fixed rather than
 * forgotten. The scaffold carried the REFERENCE APP's project: every event this
 * site fired would have landed in another product's analytics, silently and
 * correctly-looking, which is worse than measuring nothing because nothing is
 * visible and wrong data is not. Removing it was the right call at the time.
 *
 * The constant below is Fazly's own project. With the wrong-project failure
 * gone, the remaining trade-off runs the other way: this site is deployed by
 * hand, so an env-only key means one deploy from a machine without a `.env`
 * ships a site that measures nothing — and that failure is also silent.
 *
 * `VITE_POSTHOG_PROJECT_TOKEN` still overrides it, which is how a staging build
 * points at a separate project. Set it to an empty string to disable analytics
 * for a build entirely.
 *
 * That is why the token falls back with `??` while the host falls back with
 * `||`, and the difference is load-bearing. `??` gives way only to an ABSENT
 * variable, so an empty one means what it says; `||` would read "" as unset and
 * leave no way to build without analytics short of editing this file. The cost
 * is that an empty token is never a harmless blank — `.env.example` shipped one
 * uncommented, and a verbatim copy of it to `.env.local` disabled every deploy
 * built from that checkout (the whole story is in that file). The template was
 * the bug and it has been fixed there. Do not "fix" it again here by changing
 * `??` to `||`: that removes the switch rather than the trap.
 * `analytics.test.ts` pins both halves.
 *
 * A separate PostHog project from the app's, deliberately: website visitors and
 * app users are different populations and merging them makes both funnels
 * unreadable.
 *
 * Exported for its test, which has to ask what the repo COMMITS. `POSTHOG_TOKEN`
 * below is what this machine's environment resolved to, and Vite loads the
 * gitignored `.env.local` into test runs too — so a test asserting on it was
 * reporting on somebody's laptop, not on the repo.
 */
export const POSTHOG_DEFAULT_TOKEN =
  "phc_AbHM44BwDudMVA7ZNnMUxTLEyBJpLhCyfmtSMi9g2SRH";
const DEFAULT_HOST = "https://us.i.posthog.com";

export const POSTHOG_TOKEN =
  import.meta.env.VITE_POSTHOG_PROJECT_TOKEN ?? POSTHOG_DEFAULT_TOKEN;
export const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST;

if (import.meta.env.PROD && !POSTHOG_TOKEN) {
  // Only reachable when someone has explicitly set the variable to an empty
  // string. Not an error — house rule 6 says an integration is inert without
  // its key, and a build that refuses to finish over analytics is worse than
  // one that ships unmeasured. But it must not be SILENT.
  //
  // Know how quiet it still is, though. This runs when the module LOADS — in a
  // visitor's browser and in the Worker — and never during `vite build`, so the
  // terminal that ran the deploy does not show it. It tells you afterwards that
  // a build went out unmeasured; it cannot stop one. Three things stand in
  // front of it: `.env.example` ships both PostHog lines commented out so
  // copying it cannot introduce an empty token, `analytics.test.ts` goes red
  // for one with a message naming `.env.local`, and `analyticsBuildBanner` in
  // `vite.config.ts` prints which project the build is compiling in — the only
  // one of the three that speaks on the deploy path itself, which is where the
  // person who can still stop it is looking.
  console.warn(
    "[analytics] VITE_POSTHOG_PROJECT_TOKEN is empty — this build measures nothing.",
  );
}

/**
 * Analytics runs in production builds only.
 *
 * `vite dev` on localhost would otherwise file every save and every hot reload
 * as real traffic into the same project the install funnel is read from — and
 * on a site this small, a single afternoon of local work would be most of the
 * data.
 */
export const analyticsEnabled =
  import.meta.env.PROD && POSTHOG_TOKEN.length > 0;

export const posthogOptions: Partial<PostHogConfig> = {
  api_host: POSTHOG_HOST,
  // Pins the SDK's recommended defaults to a date, so a future posthog-js
  // release cannot change what is collected without an explicit bump here.
  // Among them is history-API pageview capture, which is what makes client-side
  // navigation between /privacy and /terms count as two views rather than one.
  defaults: "2026-05-30",
  // Nobody signs in on this site, so there is no identity to attach and no
  // reason to keep a cross-session profile of an anonymous reader.
  person_profiles: "never",

  /**
   * Surveys OFF, and this is a performance fix rather than a preference.
   *
   * PostHog loads `surveys.js` on every page whether or not a survey exists —
   * Lighthouse measured 34 KiB transferred, 27 KiB of it never executed, plus
   * 8ms of main thread. It was the single largest third-party cost on the site
   * and it was buying nothing: this project has no surveys and the remote
   * config already reports `surveys: false`.
   */
  disable_surveys: true,

  /**
   * Autocapture OFF.
   *
   * It was on by default and it contradicted the rule at the top of this file:
   * few, deliberate events. A single visit was filing a pageview, a pageleave,
   * an autocapture per click and the occasional rageclick — five or six rows to
   * answer a question ("how many people came, and did they tap the store
   * button") that two of them already answer.
   *
   * What is kept is exactly what that question needs: `$pageview` for visits,
   * `$pageleave` for whether they stayed, and the handful of events this site
   * declares by name. Store taps are instrumented explicitly (`web_store_click`
   * with the section that produced them), which is better data than autocapture
   * would have inferred from an anchor's text.
   *
   * The cost saved is real but small at this traffic. The clarity is the point:
   * every event in the project is now one somebody chose to send.
   */
  autocapture: false,

  /**
   * Session replay OFF, in code, whatever the dashboard says — and canvas
   * capture off underneath it.
   *
   * The tools on this site draw the visitor's own face, with the measurement
   * lines and the numbers beside it, into a plain `<canvas>`
   * (`MeasurementOverlay`), and the site and the privacy policy both say no
   * photograph reaches PostHog. Nothing here turned replay on, but nothing
   * turned it OFF either: posthog-js starts recording whenever the project's
   * remote config says replay is enabled, and the `defaults` pinned above
   * resolve `session_recording` to `{ canvasCapture: … }`, so one toggle in the
   * PostHog dashboard would have started streaming canvas frames — the face —
   * to us.i.posthog.com, which the CSP allows. Found by the 2026-10-27
   * security audit (h8-web-1), from the SDK's source: `isRecordingEnabled` is
   * `enabled_server_side && !disable_session_recording`.
   *
   * So three layers, each enough on its own:
   *   1. `disable_session_recording` — the client half of that `&&`, so the
   *      server half can never start a recorder.
   *   2. `captureCanvas.recordCanvas: false` — the client value wins over the
   *      remote `recordCanvas` (`??` in the recorder), so even a recorder
   *      started some other way never serialises canvas pixels.
   *   3. `ph-no-capture` on the canvas and on the whole result section
   *      (`FaceScanner.tsx`, `MeasurementOverlay.tsx`) — rrweb's `blockClass`,
   *      which replaces the element with an empty box of the same size.
   *
   * Turning replay on for this site is therefore a code change with a review,
   * which is where a decision about recording people's faces belongs.
   * `analytics.test.ts` pins the first two, against the real SDK as well as
   * the options object; `resultNoCapture.test.tsx` pins the third on the
   * rendered result.
   */
  disable_session_recording: true,
  session_recording: { captureCanvas: { recordCanvas: false } },

  /**
   * Every event leaves with share codes taken out of every URL in it.
   *
   * `/r/<code>`, `/p/<code>` and `/scan/<id>` put one person's identifier in
   * the path, and posthog-js copies the location onto every event it sends —
   * `lib/sharePaths.ts` has the property names and why it sweeps rather than
   * lists them. This is where the rule at the top of this file ("events carry
   * the KIND of thing shared, never which one") is enforced for the SDK's own
   * properties as well as ours (h8-web-2).
   *
   * FAILS CLOSED. A hook that throws would surface inside `capture`, which is
   * called from React effects and click handlers (house rule 6: an
   * integration must never fail what it decorates); one that returned the
   * event unscrubbed would send the code. Dropping the event is the only
   * answer that does neither, and the sweep is a pure function with its own
   * tests, so it is not expected to happen.
   */
  before_send: (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return null;
    try {
      return scrubShareCodes(event, globalThis.location?.host);
    } catch {
      return null;
    }
  },
};

/** Events this site sends. A closed list, so adding one is a decision. */
type WebEvent =
  /** A shared link opened on the WEB — i.e. by someone without the app. Carries
   *  the `kind` (`referral` or `scan`) and the `?s=` surface that produced the
   *  link, so this arrival can be joined to whatever the app logs at the other
   *  end once a share surface exists to log anything. */
  | "web_share_landing_viewed"
  /** A store button tapped: the actual handoff this site exists for.
   *
   *  Carries `from` (the surface — `hero`, `tool:<slug>`, …) and `button`:
   *  which of the two buttons was pressed, `appstore` or `play`. That property
   *  was called `store` until 2026-09 and it was read as the store the visitor
   *  REACHED, which it is not. With a OneLink configured both buttons carry
   *  the identical URL (`lib/stores.ts`) and AppsFlyer routes on the device,
   *  so the 32 Android visitors recorded as `store: appstore` in the first
   *  twelve days landed on Google Play. A review then nearly "fixed" a hero
   *  that was sending Android to Apple. The label now says what it measures;
   *  queries over older events should read `coalesce(button, store)`. */
  | "web_store_click"
  /** The install card under a tool result scrolled into view — once per mount,
   *  on intersection, never on render.
   *
   *  It exists to settle one question before anybody redesigns that card:
   *  tool-page arrivals who scanned clicked it 1 time in 79, while visitors who
   *  arrived on the homepage and then scanned clicked the SAME component 19
   *  times in 65. Without an impression, "nobody saw it" (a position problem)
   *  and "nobody wanted it" (a proposition problem) are indistinguishable, and
   *  they have opposite fixes. Firing on mount would make the ratio trivially
   *  1.0 and prove nothing, which is why this is an IntersectionObserver.
   *
   *  Carries `tool`, `from` (the same string the click sends, so view and click
   *  join on one value) and `scanned` — false where the card sits on the tools
   *  hub rather than under a result. A separate "result shown" event was
   *  considered and not added: `web_tool_scan` with `ok: true` is sent in the
   *  same tick the result renders, so it already is one. */
  | "web_tool_cta_view"
  /** "Share my result" pressed under a finished scan (`ShareResult.tsx`).
   *
   *  Carries `tool` and `method` — `image` (the drawn card went to the share
   *  sheet), `link` (the sheet with text and the link, where the browser
   *  cannot share a file), `copy` (no share sheet at all, so the text was put
   *  on the clipboard) or `none` (nothing worked) — and `done`: false when
   *  the visitor closed the sheet without sending. `done` is the browser's
   *  word, and on iOS a sheet dismissed after choosing a target still reports
   *  success, so it is an upper bound. The visits a share PRODUCES are not
   *  this event: they arrive as pageviews tagged `utm_source=share`.
   *  Never the result itself — the same rule as `web_tool_scan`. */
  | "web_tool_share"
  /** The `fazly://` deep link on /delete, which only works if installed. */
  | "web_app_open_click"
  /** Core Web Vitals from a real visit, once per page view — see lib/vitals.ts.
   *  Field data rather than a lab score: Google ranks on what devices actually
   *  measured, and a synthetic run from one machine cannot tell you that. */
  | "web_vitals"
  /** The on-device face engine finished loading, or failed to.
   *
   *  This is the one number that decides whether the free tools are a product
   *  or a spinner: 16 MB of WASM and model has to arrive before anyone can
   *  scan, and until this event existed a device that never finished the
   *  download was indistinguishable from a visitor who read the page and left.
   *  Carries `ms`, whether it was already `warm` from an earlier page in the
   *  session, and on failure the coarse `reason` — never a stack. */
  | "web_tool_engine_load"
  /** A photo was chosen on a tool page. The funnel step between arriving and
   *  scanning, and the only place the two can be told apart: a page with many
   *  arrivals and no photos has a trust problem, one with many photos and few
   *  results has an engine problem. Carries the tool slug and the same
   *  `attempt`/`type`/`kb` description of the file as `web_tool_scan` — never
   *  the file itself, and never its name. */
  | "web_tool_photo_chosen"
  /** A scan finished. Carries the tool, whether a face was found, how long the
   *  measurement took, and the OUTCOME BAND — the face shape, or the verdict
   *  band of the leading measurement.
   *
   *  The band and not the value, deliberately. A composite score of 63 next to
   *  a face-shape verdict is a measurement of one person's face, and this site
   *  has no account to attach it to and no reason to want one; a band is enough
   *  to answer the question the event exists for ("do people who get 'oblong'
   *  go on to open the app?") and is not a facial measurement of an
   *  identifiable visitor. Never the image, never the landmarks.
   *
   *  On a failure it carries `reason`, and the four values are deliberately
   *  distinct because they have four different owners:
   *
   *    `no_face`              the detector ran and found nothing — a photo problem
   *    `landmarks_incomplete` it found a face and the geometry was unusable — ours
   *    `pose_turned`          a face, refused: turned past 30°, see face/quality.ts
   *    `face_small`           a face, refused: under 100 px wide after downscale
   *    `decode`               the file never became a bitmap — a format problem
   *    `engine_*`             the WASM never loaded — a delivery problem
   *    `render`               the detector or the preview blob threw — ours
   *    `unknown`              something was thrown that was not an Error
   *
   *  The two refusals are the photo-quality gate, added 2026-09. They are
   *  refusals rather than failures — the scanner chose not to score — and they
   *  carry `yaw` (estimated head turn, whole degrees) and `face_px` so the
   *  thresholds can be moved on data. A SUCCESSFUL scan carries `yaw` too, and
   *  `pose` (`frontal` or `turned`, the latter shown with a note), which is the
   *  denominator the refusal rate needs. Nobody should write "it refuses bad
   *  photos" in copy until that rate has been watched.
   *
   *  A `decode` failure carries `heic`, because a HEIC with an empty MIME type
   *  is otherwise invisible in `type`; every scan that got past decoding
   *  carries `via` — `direct`, `resized` or `element` — the decode path that
   *  produced the bitmap (see `decodeImage`). Before 2026-09 every decode
   *  failure told the visitor no face was found; it now says it was the file.
   *
   *  The first two shared the `decode` label until 2026-09-09, which made the
   *  only failure that indicts the detector invisible inside the one that
   *  indicts the file.
   *
   *  The last two should stay at zero and are listed so that a value appearing
   *  in the dashboard is never an unexplained string. `render` catches what is
   *  left after decoding and measuring have their own handlers; if it ever
   *  climbs, the bucket has stopped meaning one thing and wants splitting the
   *  same way `decode` did. `unknown` means a non-Error was thrown, which in
   *  this path would be a browser doing something undocumented.
   *
   *  Every scan, failed or not, also carries `attempt` (which try this is in
   *  this mount), `type` and `kb` (the file's MIME type and rounded size) and,
   *  once decoding got that far, `px` (the long edge the detector actually
   *  saw, after downscaling). All four describe the FILE, never its content:
   *  they exist because Android was failing at 15% against iOS's 2.6% and
   *  nothing in the event could say why. */
  | "web_tool_scan";

/** Event properties. Same shape rule as the mobile client: no free-form
 *  strings wide enough to carry content. */
type WebEventProperties = Record<string, string | number | boolean | null>;

/**
 * Which AI assistant sent this visitor, if one did.
 *
 * ── Why this is worth a module ───────────────────────────────────────────────
 *
 * The whole GEO effort — llms.txt, the takeaways block, the structured data —
 * is a bet that assistants will start citing these pages. Without this, the bet
 * has no scoreboard: those arrivals land in PostHog as an ordinary direct or
 * referral visit and are indistinguishable from everything else.
 *
 * Registered as a SUPER PROPERTY rather than captured as its own event, so it
 * rides on every subsequent event in the session. That is what makes the useful
 * question answerable — not "how many came from Perplexity" but "do the ones
 * who came from Perplexity tap the store button more or less than the ones who
 * came from Google".
 *
 * Referrer only. No fingerprinting, nothing stored beyond what the browser
 * already sends, and it is absent for the assistants that strip the referrer —
 * which is a known undercount and better than a guess dressed as a number.
 */
const AI_REFERRERS: [RegExp, string][] = [
  [/(^|\.)chatgpt\.com$/, "chatgpt"],
  [/(^|\.)openai\.com$/, "chatgpt"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/(^|\.)claude\.ai$/, "claude"],
  [/(^|\.)anthropic\.com$/, "claude"],
  [/(^|\.)copilot\.microsoft\.com$/, "copilot"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)gemini\.google\.com$/, "gemini"],
];

/** The assistant behind a referrer URL, or null. Exported for its test. */
export function aiReferrerOf(referrer: string): string | null {
  if (!referrer) return null;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  for (const [pattern, name] of AI_REFERRERS) {
    if (pattern.test(host)) return name;
  }
  return null;
}

/**
 * `track` bound to this page's PostHog client.
 *
 * A hook rather than a module-level function because the client lives in React
 * context — which is also what makes it correct under SSR: the provider only
 * calls `init` from an effect, so on the Worker there is nothing to send to and
 * nothing tries.
 *
 * MEMOIZED, and that is not a micro-optimisation. `SharedItem` fires its landing
 * event from a `useEffect` that depends on this function; an unstable identity
 * would re-run that effect on every single render and file one
 * `web_share_landing_viewed` per render instead of one per visit — inflating the
 * top of the install funnel by an unknown multiple and billing for every copy.
 */
export function useTrack(): (
  event: WebEvent,
  properties?: WebEventProperties,
) => void {
  const posthog = usePostHog();
  return useCallback(
    (event: WebEvent, properties?: WebEventProperties) => {
      if (!analyticsEnabled) return;
      posthog?.capture(event, properties);
    },
    [posthog],
  );
}

/** Which store a button points at — kept as a union so a typo can't quietly
 *  create a third store in the funnel. */
export type Store = "appstore" | "play";
